import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import {
  CelebrationPrompt,
  CelebrationPromptByIdResponse,
  CelebrationPromptResponse,
} from '#models/db/celebration-prompt/celebration-prompt.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { ICelebrationPromptService } from './celebration-prompt.interface';
import { logger } from '#lib/logger';

@Injectable({
  providedIn: 'root',
})
export class CelebrationPromptService implements ICelebrationPromptService {
  path = 'celebration_prompts';

  getCelebrationPromptsLoading$ = new Subject<boolean>();
  celebrationPrompts$ = new BehaviorSubject<CelebrationPrompt[] | null>(null);
  currentCelebrationPrompt$ = new BehaviorSubject<CelebrationPrompt | null>(null);
  promptsExhausted$ = new BehaviorSubject<boolean>(false);

  activeCelebrationPrompt$ = new BehaviorSubject<CelebrationPrompt | null>(null);
  activeCelebrationPromptLoading$ = new Subject<boolean>();

  cycledThroughPrompts = new Set<number>();

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService
  ) {}

  getCelebrationPrompts(nextFn?: (data: CelebrationPrompt[] | null) => void, silent?: boolean) {
    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? this.getCelebrationPromptsLoading$ : undefined,
        subject: this.celebrationPrompts$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<CelebrationPromptResponse>({
          url: this.path,
        });

        // Sort by category alphabetically
        const data = response.details.sort((a: CelebrationPrompt, b: CelebrationPrompt) =>
          a.category.localeCompare(b.category)
        );

        logger.log(data, 'CelebrationPromptService.getCelebrationPrompts -> response');

        return data;
      }
    );
  }

  getCelebrationPrompt(
    id: number,
    nextFn?: (data: CelebrationPrompt | null) => void,
    silent?: boolean
  ) {
    this.intermediaryHandler.intermediary(
      {
        subject: this.activeCelebrationPrompt$,
        loading: !silent ? this.activeCelebrationPromptLoading$ : undefined,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<CelebrationPromptByIdResponse>({
          url: `${this.path}/${id}`,
        });

        logger.log(response.details, 'CelebrationPromptService.getCelebrationPrompt -> response');

        return response.details;
      }
    );
  }

  refreshCurrentPrompt(): void {
    const availablePrompts = this.celebrationPrompts$.value || [];

    if (availablePrompts.length === 0) {
      this.promptsExhausted$.next(true);
      return;
    }

    if (!availablePrompts || this.cycledThroughPrompts.size >= availablePrompts.length) {
      const lastPromptInSet =
        this.cycledThroughPrompts.size > 0 ? Array.from(this.cycledThroughPrompts).pop() : -1;

      // Reset if cycled through all available prompts.
      this.cycledThroughPrompts.clear();

      // Add the last prompt back to the set to avoid immediate repetition.
      if (lastPromptInSet !== undefined && lastPromptInSet !== -1 && availablePrompts.length > 1) {
        const lastPromptStillExists = availablePrompts.some(
          (prompt: CelebrationPrompt) => prompt.id === lastPromptInSet
        );

        if (lastPromptStillExists) {
          this.cycledThroughPrompts.add(lastPromptInSet);
        }
      }

      this.promptsExhausted$.next(false);
      this.selectRandomPrompt(availablePrompts);
    } else {
      this.selectRandomPrompt(availablePrompts);
    }
  }

  private selectRandomPrompt(availablePrompts: CelebrationPrompt[]): void {
    // Check if there are any prompts available that haven't been cycled through
    const uncycledPrompts = availablePrompts.filter(
      (prompt: CelebrationPrompt) => !this.cycledThroughPrompts.has(prompt.id)
    );

    if (uncycledPrompts.length === 0) {
      this.currentCelebrationPrompt$.next(null);
      this.promptsExhausted$.next(true);
      return;
    }

    let idx;
    let prompt: CelebrationPrompt;

    do {
      idx = Math.floor(Math.random() * availablePrompts.length);
      prompt = availablePrompts[idx];
    } while (this.cycledThroughPrompts.has(prompt.id));

    this.cycledThroughPrompts.add(prompt.id);
    this.currentCelebrationPrompt$.next(prompt);

    logger.log(this.cycledThroughPrompts, 'CelebrationPromptService.selectRandomPrompt -> idx');
  }

  resetActiveCelebrationPrompt() {
    this.activeCelebrationPrompt$.next(null);
  }

  resetCelebrationPrompts() {
    this.cycledThroughPrompts.clear();
    this.promptsExhausted$.next(false);
    this.celebrationPrompts$.next(null);
    this.currentCelebrationPrompt$.next(null);
    this.activeCelebrationPrompt$.next(null);
    logger.log('complete', 'CelebrationPromptService.resetCelebrationPrompts');
  }
}
